import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./MediaGalleryPanel.css";
import { WhatsAppImportsPanel } from "./WhatsAppImportsPanel";
import { getApiBase } from "../apiBase";
import { FEATURE_FLAGS } from "../featureFlags";

const API_BASE = getApiBase();
const MEDIA_COPY = {
  en: {
    unavailableVault:
      "Your memory vault is not fully available in this environment yet. Please try again a little later.",
    readyWhenYouAre: "Ready when you are",
    updatedPrefix: "Updated",
    categories: {
      property: "Property space",
      legal: "Private document",
      financial: "Financial overview",
      personal: "Personal space",
    },
    subtitles: {
      releaseLocked: "Release locked",
      sharedWithFamily: "Shared with family",
      privateMemories: "Private memories",
      privateDocument: "Private document",
    },
    templates: [
      { label: "Legal documents", name: "Legal documents" },
      { label: "Property", name: "Property records" },
      { label: "Tax documents", name: "Tax documents" },
    ],
    memorySingle: "memory",
    memoryPlural: "memories",
    loadFoldersFailed: "Could not load folders",
    loadMediaFailed: "Could not load media",
    loadSharedFailed: "Could not load shared memories",
    finishSavingFailed: "Could not finish saving this memory",
    sharedNowNotice: (name) => `${name} will see this in Shared with you and will get an email notification.`,
    sharedSignInNotice: (name) => `${name} will see this in Shared with you after they sign in.`,
    removeConfirm: "Remove this memory from your vault?",
    deleteFailed: "Delete failed",
    ungrouped: "Ungrouped",
    sectionAria: "Memory vault",
    headerTitle: "Memory spaces for the people you love",
    headerBody:
      "Keep your photos, documents, and important moments together in calm, easy-to-open spaces. Everything stays encrypted and secure, and you choose whether a trusted family member sees something right away or only after verification.",
    highlightsAria: "Media workflow highlights",
    highlightsKicker: "At a glance",
    librarySummaryAria: "Media library summary",
    folderOne: "Folder",
    folderMany: "Folders",
    memoryOne: "Memory",
    memoryMany: "Memories",
    tagged: "Tagged",
    releaseLocked: "Release-locked",
    highlightsBody:
      "Group memories by topic, person, or document type. Add a note, choose who it is for, and decide whether it is shared now or kept for later.",
    pills: ["🔒 Encrypted & secure", "👨‍👩‍👧 Trusted family sharing", "⏳ Release timing controls"],
    foldersUnavailable:
      "Custom spaces are not available in this environment yet. You can still keep memories together in the main library for now.",
    privacyAria: "Privacy reassurance",
    privacyTitle: "Your privacy matters",
    privacyBody:
      "Your memories stay encrypted, only you control access, and nothing is shared without your permission.",
    privacyPoints: [
      "Your memories stay private by default.",
      "You choose whether something is shared now or later.",
      "Trusted family access only happens on your terms.",
    ],
    scopeAria: "Document library views",
    scopeOwned: "Your documents",
    scopeShared: "Shared with you",
    folderFiltersAria: "Folder filters",
    view: "View",
    all: "All",
    deleteFolder: "Delete folder",
    deleteFolderAria: (name) => `Delete folder ${name}`,
    currentSharedTitle: "Shared with you",
    currentOwnedTitle: "Your documents",
    currentUngroupedTitle: "Ungrouped memories",
    currentFolderFallback: "Folder memories",
    currentSharedDescription:
      "Memories that other family members shared with you appear here, along with any note they included.",
    currentBrowserDescription:
      "Browse by space first, then open one to step into the memories inside it.",
    currentViewDescription: (countLabel) =>
      `${countLabel} in this view. Open any memory for a larger preview and the note that belongs with it.`,
    backToFolders: "Back to folders",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    sharedEmpty:
      "Nothing has been shared with you yet. When a family member sends something your way, it will show up here with their note.",
    sharedMemoryFallback: "Shared memory",
    previewUnavailable: "Preview unavailable",
    untitled: "Untitled",
    sharedBy: (name) => `Shared by ${name}`,
    forYou: "For you",
    onboardingAria: "Memory vault getting started",
    onboardingKicker: "Getting started",
    onboardingTitle: "Start building your memory vault",
    onboardingPoints: [
      "Add important documents you want to keep easy to find.",
      "Store personal memories alongside family paperwork.",
      "Organize gently with spaces only when you need them.",
    ],
    addFirstMemory: "Add your first memory",
    emptyFolder: "Empty folder",
    openFolder: "Open folder",
    noMemoriesYet: "No memories yet. Add a memory or create a space whenever you are ready.",
    noMemoriesInView: "No memories in this view yet. Add a photo, video, or document when you are ready.",
    memoryFallback: "Memory",
    deleteItemAria: (name) => `Delete ${name || "item"}`,
    forPerson: (name) => `For ${name}`,
    afterRelease: "After release",
    recipientAccessAfterRelease: "Recipient access after release",
    sourceWhatsApp: "WhatsApp",
    actionsAria: "Memory vault actions",
    actionsTitle: "Library actions",
    actionsBody:
      "Create spaces only when you need them. Start simple, then add memories when you are ready.",
    hideSpaceCreator: "Hide space creator",
    createSpace: "Create a space",
    hideAddMemory: "Hide add memory",
    addMemory: "Add memory",
    nameSpace: "Name a space",
    spacePlaceholder: "e.g. Family documents",
    creating: "Creating…",
    createSpaceAction: "Create space",
    folderCreateNote: "Spaces save only the name. Any note is saved with the memories you add inside.",
    quickIdeasAria: "Quick folder ideas",
    quickStarts: "Quick starts:",
    uploadToSpace: "Add to this space",
    uploadTitle: "Add a memory",
    uploadBody:
      "Add photos, videos, or documents, then choose whether they stay private or are meant for someone specific.",
    addTo: "Add to",
    ungroupedNoFolder: "Ungrouped (no folder)",
    adding: "Adding…",
    addingDisabled: "Adding disabled",
    chooseMemories: "Choose memories",
    addingUnavailable: "Adding memories is not available in this environment yet.",
    dragDrop: "or drag and drop here",
    tagRecipientOptional: "Tag for recipient (optional)",
    tagRecipientHint:
      "Pick a recipient if these memories are meant for someone. You can also save that choice for this space so it is ready next time.",
    notTagged: "Not tagged",
    savedRecipient: "Saved recipient",
    releaseCheckbox: "Recipient can access this only after your account is released",
    shareBehaviorDelayed:
      "This stays private for now. They will only see it after your release workflow is approved.",
    shareBehaviorNow:
      "This will appear in their Shared with you section right away, and they will get an email notification.",
    noteForMemories: "Note for these memories",
    noteHint: "This note is saved with the memories you add in this batch.",
    notePlaceholder: "Add the note or memory that should stay with these memories.",
    saveRecipientForSpace: "Save this recipient for the space",
    saving: "Saving…",
    clearSavedRecipient: "Clear saved recipient",
    deleteFolderQuestion: "Delete folder?",
    deleteFolderBody: (name) =>
      `This will permanently delete ${name} and every file stored in it. This cannot be undone.`,
    cancel: "Cancel",
    deleting: "Deleting…",
    deleteFolderAction: "Delete folder",
    lightboxAria: "Media viewer",
    close: "Close",
    sharedByLabel: (name) => `Shared by: ${name}`,
    folderLabel: (name) => `Folder: ${name}`,
    taggedLabel: (name) => `Tagged: ${name}`,
    releaseVerification:
      "Recipient access is limited until account release (verification).",
    importedWhatsApp: "Imported from WhatsApp",
    removeFromGallery: "Remove from gallery",
  },
  fr: {
    unavailableVault:
      "Votre coffre à souvenirs n'est pas encore entièrement disponible dans cet environnement. Veuillez réessayer un peu plus tard.",
    readyWhenYouAre: "Prêt lorsque vous l'êtes",
    updatedPrefix: "Mis à jour",
    categories: {
      property: "Espace immobilier",
      legal: "Document privé",
      financial: "Aperçu financier",
      personal: "Espace personnel",
    },
    subtitles: {
      releaseLocked: "Diffusion verrouillée",
      sharedWithFamily: "Partagé avec la famille",
      privateMemories: "Souvenirs privés",
      privateDocument: "Document privé",
    },
    templates: [
      { label: "Documents juridiques", name: "Documents juridiques" },
      { label: "Propriété", name: "Dossiers de propriété" },
      { label: "Documents fiscaux", name: "Documents fiscaux" },
    ],
    memorySingle: "souvenir",
    memoryPlural: "souvenirs",
    loadFoldersFailed: "Impossible de charger les espaces",
    loadMediaFailed: "Impossible de charger les souvenirs",
    loadSharedFailed: "Impossible de charger les souvenirs partagés",
    finishSavingFailed: "Impossible d'enregistrer ce souvenir",
    sharedNowNotice: (name) => `${name} le verra dans Partagé avec vous et recevra une notification par courriel.`,
    sharedSignInNotice: (name) => `${name} le verra dans Partagé avec vous après sa connexion.`,
    removeConfirm: "Retirer ce souvenir de votre coffre ?",
    deleteFailed: "Échec de la suppression",
    ungrouped: "Non classé",
    sectionAria: "Coffre à souvenirs",
    headerTitle: "Des espaces souvenirs pour les personnes que vous aimez",
    headerBody:
      "Gardez vos photos, documents et moments importants ensemble dans des espaces calmes et faciles à ouvrir. Tout reste chiffré et sécurisé, et vous choisissez si un proche de confiance voit quelque chose tout de suite ou seulement après vérification.",
    highlightsAria: "Repères du parcours média",
    highlightsKicker: "En bref",
    librarySummaryAria: "Résumé de la médiathèque",
    folderOne: "Espace",
    folderMany: "Espaces",
    memoryOne: "Souvenir",
    memoryMany: "Souvenirs",
    tagged: "Identifiés",
    releaseLocked: "Verrouillés",
    highlightsBody:
      "Regroupez vos souvenirs par sujet, personne ou type de document. Ajoutez une note, choisissez le destinataire et décidez s'il est partagé maintenant ou gardé pour plus tard.",
    pills: ["🔒 Chiffré et sécurisé", "👨‍👩‍👧 Partage avec la famille de confiance", "⏳ Contrôle du moment de diffusion"],
    foldersUnavailable:
      "Les espaces personnalisés ne sont pas encore disponibles dans cet environnement. Vous pouvez tout de même garder vos souvenirs dans la bibliothèque principale pour l'instant.",
    privacyAria: "Rappel de confidentialité",
    privacyTitle: "Votre vie privée compte",
    privacyBody:
      "Vos souvenirs restent chiffrés, vous seul contrôlez l'accès, et rien n'est partagé sans votre permission.",
    privacyPoints: [
      "Vos souvenirs restent privés par défaut.",
      "Vous choisissez si quelque chose est partagé maintenant ou plus tard.",
      "L'accès familial de confiance n'arrive que selon vos choix.",
    ],
    scopeAria: "Vues de la médiathèque",
    scopeOwned: "Vos documents",
    scopeShared: "Partagé avec vous",
    folderFiltersAria: "Filtres d'espaces",
    view: "Vue",
    all: "Tout",
    deleteFolder: "Supprimer l'espace",
    deleteFolderAria: (name) => `Supprimer l'espace ${name}`,
    currentSharedTitle: "Partagé avec vous",
    currentOwnedTitle: "Vos documents",
    currentUngroupedTitle: "Souvenirs non classés",
    currentFolderFallback: "Souvenirs de l'espace",
    currentSharedDescription:
      "Les souvenirs que d'autres membres de la famille ont partagés avec vous apparaissent ici, avec la note qu'ils ont ajoutée.",
    currentBrowserDescription:
      "Parcourez d'abord les espaces, puis ouvrez-en un pour entrer dans les souvenirs qu'il contient.",
    currentViewDescription: (countLabel) =>
      `${countLabel} dans cette vue. Ouvrez un souvenir pour une prévisualisation plus grande et la note qui l'accompagne.`,
    backToFolders: "Retour aux espaces",
    refresh: "Actualiser",
    refreshing: "Actualisation…",
    sharedEmpty:
      "Rien n'a encore été partagé avec vous. Lorsqu'un proche vous envoie quelque chose, cela apparaîtra ici avec sa note.",
    sharedMemoryFallback: "Souvenir partagé",
    previewUnavailable: "Prévisualisation indisponible",
    untitled: "Sans titre",
    sharedBy: (name) => `Partagé par ${name}`,
    forYou: "Pour vous",
    onboardingAria: "Premiers pas du coffre à souvenirs",
    onboardingKicker: "Pour commencer",
    onboardingTitle: "Commencez à bâtir votre coffre à souvenirs",
    onboardingPoints: [
      "Ajoutez les documents importants que vous voulez garder faciles à retrouver.",
      "Conservez les souvenirs personnels à côté des papiers familiaux.",
      "Organisez doucement avec des espaces seulement quand vous en avez besoin.",
    ],
    addFirstMemory: "Ajouter votre premier souvenir",
    emptyFolder: "Espace vide",
    openFolder: "Ouvrir l'espace",
    noMemoriesYet: "Aucun souvenir pour l'instant. Ajoutez un souvenir ou créez un espace lorsque vous serez prêt.",
    noMemoriesInView: "Aucun souvenir dans cette vue pour l'instant. Ajoutez une photo, une vidéo ou un document lorsque vous serez prêt.",
    memoryFallback: "Souvenir",
    deleteItemAria: (name) => `Supprimer ${name || "l'élément"}`,
    forPerson: (name) => `Pour ${name}`,
    afterRelease: "Après diffusion",
    recipientAccessAfterRelease: "Accès du destinataire après diffusion",
    sourceWhatsApp: "WhatsApp",
    actionsAria: "Actions du coffre à souvenirs",
    actionsTitle: "Actions de la bibliothèque",
    actionsBody:
      "Créez des espaces seulement lorsque vous en avez besoin. Commencez simplement, puis ajoutez des souvenirs à votre rythme.",
    hideSpaceCreator: "Masquer la création d'espace",
    createSpace: "Créer un espace",
    hideAddMemory: "Masquer l'ajout",
    addMemory: "Ajouter un souvenir",
    nameSpace: "Nommer un espace",
    spacePlaceholder: "p. ex. Documents familiaux",
    creating: "Création…",
    createSpaceAction: "Créer l'espace",
    folderCreateNote:
      "Les espaces n'enregistrent que le nom. Toute note est enregistrée avec les souvenirs ajoutés à l'intérieur.",
    quickIdeasAria: "Idées rapides d'espaces",
    quickStarts: "Départs rapides :",
    uploadToSpace: "Ajouter à cet espace",
    uploadTitle: "Ajouter un souvenir",
    uploadBody:
      "Ajoutez des photos, vidéos ou documents, puis choisissez s'ils restent privés ou sont destinés à quelqu'un en particulier.",
    addTo: "Ajouter à",
    ungroupedNoFolder: "Non classé (aucun espace)",
    adding: "Ajout…",
    addingDisabled: "Ajout désactivé",
    chooseMemories: "Choisir les souvenirs",
    addingUnavailable: "L'ajout de souvenirs n'est pas disponible dans cet environnement pour le moment.",
    dragDrop: "ou glissez-déposez ici",
    tagRecipientOptional: "Identifier un destinataire (optionnel)",
    tagRecipientHint:
      "Choisissez un destinataire si ces souvenirs lui sont destinés. Vous pouvez aussi enregistrer ce choix pour cet espace afin qu'il soit prêt la prochaine fois.",
    notTagged: "Non identifié",
    savedRecipient: "Destinataire enregistré",
    releaseCheckbox: "Le destinataire ne peut y accéder qu'après la diffusion de votre compte",
    shareBehaviorDelayed:
      "Cela reste privé pour l'instant. La personne ne le verra qu'après l'approbation de votre processus de diffusion.",
    shareBehaviorNow:
      "Cela apparaîtra immédiatement dans sa section Partagé avec vous, et elle recevra une notification par courriel.",
    noteForMemories: "Note pour ces souvenirs",
    noteHint: "Cette note est enregistrée avec les souvenirs ajoutés dans ce lot.",
    notePlaceholder: "Ajoutez la note ou le souvenir qui doit accompagner ces éléments.",
    saveRecipientForSpace: "Enregistrer ce destinataire pour l'espace",
    saving: "Enregistrement…",
    clearSavedRecipient: "Effacer le destinataire enregistré",
    deleteFolderQuestion: "Supprimer l'espace ?",
    deleteFolderBody: (name) =>
      `Cela supprimera définitivement ${name} et tous les fichiers qu'il contient. Cette action est irréversible.`,
    cancel: "Annuler",
    deleting: "Suppression…",
    deleteFolderAction: "Supprimer l'espace",
    lightboxAria: "Visionneuse de médias",
    close: "Fermer",
    sharedByLabel: (name) => `Partagé par : ${name}`,
    folderLabel: (name) => `Espace : ${name}`,
    taggedLabel: (name) => `Identifié : ${name}`,
    releaseVerification:
      "L'accès du destinataire est limité jusqu'à la diffusion du compte (vérification).",
    importedWhatsApp: "Importé depuis WhatsApp",
    removeFromGallery: "Retirer de la galerie",
  },
};

function friendlyMediaError(message) {
  const m = String(message || "");
  if (/DYNAMODB_MEDIA_TABLE|MEDIA_BUCKET|Missing.*environment variable/i.test(m)) {
    return "";
  }
  return m;
}

function formatUpdatedLabel(value, locale, copy) {
  if (!value) return copy.readyWhenYouAre;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.readyWhenYouAre;
  return `${copy.updatedPrefix} ${new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)}`;
}

function inferFolderCategory(name, copy) {
  const n = String(name || "").toLowerCase();
  if (/property|mortgage|real estate|home|house/.test(n)) {
    return { icon: "🏠", label: copy.categories.property };
  }
  if (/legal|tax|document|passport|will|contract/.test(n)) {
    return { icon: "📄", label: copy.categories.legal };
  }
  if (/financial|bank|loan|investment|rrsp|rrif|tfsa|resp|money/.test(n)) {
    return { icon: "💰", label: copy.categories.financial };
  }
  return { icon: "📁", label: copy.categories.personal };
}

function inferFolderSubtitle(card, copy) {
  if (card.recipientAfterRelease) {
    return copy.subtitles.releaseLocked;
  }
  if (card.defaultTaggedDisplayName) {
    return copy.subtitles.sharedWithFamily;
  }
  return card.id === "ungrouped" ? copy.subtitles.privateMemories : copy.subtitles.privateDocument;
}

function guessContentType(file) {
  if (file.type && file.type !== "application/octet-stream") {
    return file.type;
  }
  const n = file.name.toLowerCase();
  if (n.endsWith(".png")) return "image/png";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
  if (n.endsWith(".gif")) return "image/gif";
  if (n.endsWith(".webp")) return "image/webp";
  if (n.endsWith(".heic") || n.endsWith(".heif")) return "image/heic";
  if (n.endsWith(".mp4")) return "video/mp4";
  if (n.endsWith(".webm")) return "video/webm";
  if (n.endsWith(".mov")) return "video/quicktime";
  return "";
}

async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function formatMemoryCount(count, copy) {
  return `${count} ${count === 1 ? copy.memorySingle : copy.memoryPlural}`;
}

export function MediaGalleryPanel({
  authToken,
  locale = "en",
  tagOptions = [],
  onMediaLibraryChanged,
  reloadNonce = 0,
}) {
  const copy = MEDIA_COPY[String(locale || "").toLowerCase().startsWith("fr") ? "fr" : "en"];
  const [items, setItems] = useState([]);
  const [libraryIndexItems, setLibraryIndexItems] = useState([]);
  const [sharedItems, setSharedItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [foldersConfigured, setFoldersConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [mediaNotice, setMediaNotice] = useState("");
  const [sharedNotice, setSharedNotice] = useState("");
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const [tagForUpload, setTagForUpload] = useState("");
  const [recipientAfterRelease, setRecipientAfterRelease] = useState(false);
  const [memoryMessageForUpload, setMemoryMessageForUpload] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [libraryScope, setLibraryScope] = useState("owned");

  /** `all` | `ungrouped` | folderId */
  const [galleryFilter, setGalleryFilter] = useState("all");
  /** Upload target: `""` = legacy ungrouped key, else folder UUID */
  const [uploadFolderId, setUploadFolderId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  /** `null` or `{ folderId, name }` when confirming folder delete */
  const [folderDeleteConfirm, setFolderDeleteConfirm] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [savingFolderDefaults, setSavingFolderDefaults] = useState(false);
  const [showFolderComposer, setShowFolderComposer] = useState(false);
  const [showUploadComposer, setShowUploadComposer] = useState(false);

  /** Avoid overwriting manual tag edits when only `folders` array reference changes. */
  const lastFolderRecipientSignature = useRef("");

  const folderNameById = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => m.set(f.folderId, f.name));
    return m;
  }, [folders]);

  const loadFolders = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/media/folders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadFoldersFailed);
      }
      setFolders(Array.isArray(data.folders) ? data.folders : []);
      setFoldersConfigured(data.foldersConfigured !== false);
    } catch {
      setFolders([]);
      setFoldersConfigured(false);
    }
  }, [authToken, copy.loadFoldersFailed]);

  const loadMedia = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setLocalError("");
    setMediaNotice("");
    try {
      const q =
        galleryFilter === "all" ? "" : `?folder=${encodeURIComponent(galleryFilter)}`;
      const res = await fetch(`${API_BASE}/api/media${q}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadMediaFailed);
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setMediaAvailable(data.mediaAvailable !== false);
      setMediaNotice(typeof data.notice === "string" ? data.notice : "");
      if (typeof onMediaLibraryChanged === "function") {
        onMediaLibraryChanged();
      }
    } catch (e) {
      setLocalError(friendlyMediaError(e.message) || copy.unavailableVault);
      setMediaAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [authToken, galleryFilter, onMediaLibraryChanged, copy.loadMediaFailed, copy.unavailableVault]);

  const loadLibraryIndex = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/media`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadMediaFailed);
      }
      setLibraryIndexItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setLibraryIndexItems([]);
    }
  }, [authToken, copy.loadMediaFailed]);

  const loadSharedMedia = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setLocalError("");
    setSharedNotice("");
    try {
      const res = await fetch(`${API_BASE}/api/media/shared`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadSharedFailed);
      }
      setSharedItems(Array.isArray(data.items) ? data.items : []);
      setSharedNotice(typeof data.notice === "string" ? data.notice : "");
    } catch (e) {
      setLocalError(friendlyMediaError(e.message) || copy.unavailableVault);
      setSharedItems([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, copy.loadSharedFailed, copy.unavailableVault]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (libraryScope === "shared") {
      loadSharedMedia();
      return;
    }
    loadMedia();
  }, [libraryScope, loadMedia, loadSharedMedia]);

  useEffect(() => {
    loadLibraryIndex();
  }, [loadLibraryIndex]);

  useEffect(() => {
    if (!tagForUpload) {
      return;
    }
    const stillValid = tagOptions.some((o) => o.id === tagForUpload);
    if (!stillValid) {
      setTagForUpload("");
      setRecipientAfterRelease(false);
    }
  }, [tagOptions, tagForUpload]);

  useEffect(() => {
    if (!authToken || !reloadNonce) {
      return;
    }
    loadFolders();
    loadLibraryIndex();
    if (libraryScope === "shared") {
      loadSharedMedia();
    } else {
      loadMedia();
    }
  }, [reloadNonce, authToken, libraryScope, loadFolders, loadLibraryIndex, loadMedia, loadSharedMedia]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  useEffect(() => {
    if (libraryScope !== "owned") {
      return;
    }
    if (galleryFilter !== "all") {
      setShowUploadComposer(true);
    }
  }, [galleryFilter, libraryScope]);

  useEffect(() => {
    if (!folderDeleteConfirm) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !deletingFolder) setFolderDeleteConfirm(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [folderDeleteConfirm, deletingFolder]);

  const selectedUploadFolder = useMemo(
    () => folders.find((f) => f.folderId === uploadFolderId) || null,
    [folders, uploadFolderId]
  );

  useEffect(() => {
    if (!foldersConfigured) return;
    if (!uploadFolderId) {
      setTagForUpload("");
      setRecipientAfterRelease(false);
      lastFolderRecipientSignature.current = "";
      return;
    }
    const f = folders.find((x) => x.folderId === uploadFolderId);
    if (!f) return;
    const sig = `${f.folderId}|${f.defaultTaggedUserId || ""}|${f.defaultRecipientAfterRelease ? "1" : "0"}|${f.updatedAt || f.createdAt || ""}`;
    if (lastFolderRecipientSignature.current === sig) {
      return;
    }
    lastFolderRecipientSignature.current = sig;
    if (f.defaultTaggedUserId) {
      setTagForUpload(f.defaultTaggedUserId);
      setRecipientAfterRelease(Boolean(f.defaultRecipientAfterRelease));
    } else {
      setTagForUpload("");
      setRecipientAfterRelease(false);
    }
  }, [uploadFolderId, folders, foldersConfigured]);

  const saveFolderRecipientDefaults = async () => {
    if (!authToken || !uploadFolderId) return;
    setSavingFolderDefaults(true);
    setLocalError("");
    try {
      const patchBody = tagForUpload
        ? (() => {
            const opt = tagOptions.find((o) => o.id === tagForUpload);
            return {
              defaultTaggedUserId: tagForUpload,
              defaultTaggedDisplayName: opt ? opt.label : tagForUpload,
              defaultRecipientAfterRelease: recipientAfterRelease,
            };
          })()
        : { clearDefaultRecipient: true };
      const res = await fetch(
        `${API_BASE}/api/media/folders/${encodeURIComponent(uploadFolderId)}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(patchBody),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not save folder defaults");
      }
      await loadFolders();
      lastFolderRecipientSignature.current = "";
    } catch (e) {
      setLocalError(friendlyMediaError(e.message));
    } finally {
      setSavingFolderDefaults(false);
    }
  };

  const clearFolderRecipientDefaults = async () => {
    if (!authToken || !uploadFolderId) return;
    setSavingFolderDefaults(true);
    setLocalError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/media/folders/${encodeURIComponent(uploadFolderId)}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ clearDefaultRecipient: true }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not clear folder defaults");
      }
      await loadFolders();
      lastFolderRecipientSignature.current = "";
    } catch (e) {
      setLocalError(friendlyMediaError(e.message));
    } finally {
      setSavingFolderDefaults(false);
    }
  };

  const createFolder = async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed || !authToken) return;
    const norm = trimmed.toLowerCase();
    const already = folders.find((f) => String(f.name || "").trim().toLowerCase() === norm);
    if (already) {
      setUploadFolderId(already.folderId);
      setGalleryFilter(already.folderId);
      setNewFolderName("");
      return;
    }
    setCreatingFolder(true);
    setLocalError("");
    try {
      const res = await fetch(`${API_BASE}/api/media/folders`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not create folder");
      }
      await loadFolders();
      setNewFolderName("");
      if (data.folderId) {
        setUploadFolderId(data.folderId);
        setGalleryFilter(data.folderId);
      }
    } catch (e) {
      setLocalError(friendlyMediaError(e.message));
    } finally {
      setCreatingFolder(false);
    }
  };

  const requestDeleteFolder = (folderId, name) => {
    setFolderDeleteConfirm({ folderId, name });
  };

  const cancelDeleteFolder = () => {
    if (!deletingFolder) setFolderDeleteConfirm(null);
  };

  const confirmDeleteFolder = async () => {
    if (!authToken || !folderDeleteConfirm) return;
    const { folderId } = folderDeleteConfirm;
    setDeletingFolder(true);
    setLocalError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/media/folders/${encodeURIComponent(folderId)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 204) {
        throw new Error(data.message || "Could not delete folder");
      }
      setFolderDeleteConfirm(null);
      if (galleryFilter === folderId) {
        setGalleryFilter("all");
      }
      if (uploadFolderId === folderId) {
        setUploadFolderId("");
      }
      await loadFolders();
      await loadMedia();
      await loadLibraryIndex();
    } catch (e) {
      setLocalError(friendlyMediaError(e.message));
    } finally {
      setDeletingFolder(false);
    }
  };

  const uploadOne = async (file) => {
    const contentType = guessContentType(file);
    if (!contentType.startsWith("image/") && !contentType.startsWith("video/")) {
      throw new Error(`${file.name}: use a common image or video type (jpg, png, mp4, …).`);
    }
    const contentSha256 = await sha256Hex(file);
    const presBody = {
      fileName: file.name,
      contentType,
      contentSha256,
      sizeBytes: file.size,
    };
    if (uploadFolderId) {
      presBody.folderId = uploadFolderId;
    }
    const presRes = await fetch(`${API_BASE}/api/media/presign`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(presBody),
    });
    const pres = await presRes.json().catch(() => ({}));
    if (!presRes.ok) {
      throw new Error(pres.message || "Could not start adding this memory");
    }
    const putRes = await fetch(pres.uploadUrl, {
      method: "PUT",
      headers: pres.headers,
      body: file,
    });
    if (!putRes.ok) {
      throw new Error("Could not add this memory right now.");
    }
    let taggedUserId;
    let taggedDisplayName;
    if (tagForUpload) {
      const opt = tagOptions.find((o) => o.id === tagForUpload);
      taggedUserId = tagForUpload;
      taggedDisplayName = opt ? opt.label : tagForUpload;
    }
    const folderIdForConfirm = pres.folderId || uploadFolderId || undefined;
    const confRes = await fetch(`${API_BASE}/api/media/confirm`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mediaId: pres.mediaId,
        s3Key: pres.s3Key,
        fileName: file.name,
        contentType,
        contentSha256,
        ...(memoryMessageForUpload.trim()
          ? { memoryMessage: memoryMessageForUpload.trim() }
          : {}),
        ...(folderIdForConfirm ? { folderId: folderIdForConfirm } : {}),
        ...(taggedUserId ? { taggedUserId, taggedDisplayName } : {}),
        ...(taggedUserId && recipientAfterRelease ? { recipientAccessAfterRelease: true } : {}),
      }),
    });
    const saved = await confRes.json().catch(() => ({}));
    if (!confRes.ok) {
      throw new Error(saved.message || copy.finishSavingFailed);
    }
    if (taggedDisplayName && !recipientAfterRelease) {
      setMediaNotice(
        saved.shareNotificationSent
          ? copy.sharedNowNotice(taggedDisplayName)
          : copy.sharedSignInNotice(taggedDisplayName)
      );
    }
    return saved;
  };

  const onPickFiles = async (fileList) => {
    if (!authToken || !fileList?.length) return;
    setLocalError("");
    setUploading(true);
    try {
      for (const file of fileList) {
        await uploadOne(file);
      }
      setMemoryMessageForUpload("");
      await loadMedia();
      await loadLibraryIndex();
    } catch (e) {
      setLocalError(friendlyMediaError(e.message) || copy.unavailableVault);
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = async (mediaId) => {
    if (!authToken || !window.confirm(copy.removeConfirm)) {
      return;
    }
    setLocalError("");
    try {
      const res = await fetch(`${API_BASE}/api/media/${encodeURIComponent(mediaId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || copy.deleteFailed);
      }
      setLightbox((prev) => (prev?.mediaId === mediaId ? null : prev));
      await loadMedia();
      await loadLibraryIndex();
    } catch (e) {
      setLocalError(friendlyMediaError(e.message) || copy.unavailableVault);
    }
  };

  const isVideo = (ct) => String(ct || "").toLowerCase().startsWith("video/");

  const uploadsDisabled = uploading || !mediaAvailable;
  const showingFolderBrowser = libraryScope === "owned" && galleryFilter === "all";

  const folderCards = useMemo(() => {
    const counts = new Map();
    const previews = new Map();
    const updatedTimes = new Map();

    libraryIndexItems.forEach((item) => {
      const key = item.folderId || "ungrouped";
      counts.set(key, (counts.get(key) || 0) + 1);
      const itemUpdatedAt = item.updatedAt || item.createdAt || "";
      if (itemUpdatedAt) {
        const existing = updatedTimes.get(key);
        if (!existing || new Date(itemUpdatedAt).getTime() > new Date(existing).getTime()) {
          updatedTimes.set(key, itemUpdatedAt);
        }
      }
      if (!previews.has(key)) {
        previews.set(key, []);
      }
      const next = previews.get(key);
      if (next.length < 3) {
        next.push(item);
      }
    });

    const cards = folders.map((folder) => {
      const category = inferFolderCategory(folder.name, copy);
      const card = {
        id: folder.folderId,
        name: folder.name,
        count: counts.get(folder.folderId) || 0,
        previews: previews.get(folder.folderId) || [],
        defaultTaggedDisplayName: folder.defaultTaggedDisplayName || "",
        recipientAfterRelease: Boolean(folder.defaultRecipientAfterRelease),
        updatedAt:
          folder.updatedAt || folder.createdAt || updatedTimes.get(folder.folderId) || "",
        categoryIcon: category.icon,
        categoryLabel: category.label,
      };
      return { ...card, subtitle: inferFolderSubtitle(card, copy) };
    });

    const ungroupedCount = counts.get("ungrouped") || 0;
    if (ungroupedCount > 0 || cards.length === 0) {
      const ungroupedCard = {
        id: "ungrouped",
        name: copy.ungrouped,
        count: ungroupedCount,
        previews: previews.get("ungrouped") || [],
        defaultTaggedDisplayName: "",
        recipientAfterRelease: false,
        updatedAt: updatedTimes.get("ungrouped") || "",
        categoryIcon: "📁",
        categoryLabel: copy.subtitles.privateMemories,
      };
      cards.push({
        ...ungroupedCard,
        subtitle: inferFolderSubtitle(ungroupedCard, copy),
      });
    }

    return cards.map((card) => ({
      ...card,
      updatedLabel: formatUpdatedLabel(card.updatedAt, locale, copy),
    }));
  }, [folders, libraryIndexItems, copy, locale]);

  const currentGalleryTitle =
    libraryScope === "shared"
      ? copy.currentSharedTitle
      : galleryFilter === "all"
        ? copy.currentOwnedTitle
        : galleryFilter === "ungrouped"
          ? copy.currentUngroupedTitle
          : folderNameById.get(galleryFilter) || copy.currentFolderFallback;

  const currentGalleryDescription =
    libraryScope === "shared"
      ? copy.currentSharedDescription
      : showingFolderBrowser
        ? copy.currentBrowserDescription
        : copy.currentViewDescription(formatMemoryCount(items.length, copy));

  const mediaSummary = useMemo(() => {
    const totalMemories = libraryIndexItems.length;
    const taggedMemories = libraryIndexItems.filter((item) => item.taggedUserId).length;
    const releaseLockedMemories = libraryIndexItems.filter(
      (item) => item.recipientAccessAfterRelease
    ).length;
    return {
      totalMemories,
      taggedMemories,
      releaseLockedMemories,
      folderCount: folders.length,
    };
  }, [folders.length, libraryIndexItems]);

  const showOnboardingCard = libraryScope === "owned" && mediaSummary.totalMemories < 3;

  return (
    <section className="panel media-gallery-panel" aria-label={copy.sectionAria}>
      <div className="panel-head media-gallery-head">
        <div className="media-gallery-head-copy">
          <h2>{copy.headerTitle}</h2>
          <p>{copy.headerBody}</p>
        </div>
        <aside className="media-gallery-head-highlights" aria-label={copy.highlightsAria}>
          <p className="media-gallery-head-kicker">{copy.highlightsKicker}</p>
          <div className="media-gallery-head-stats" aria-label={copy.librarySummaryAria}>
            <div className="media-gallery-head-stat">
              <strong>{mediaSummary.folderCount}</strong>
              <span>{mediaSummary.folderCount === 1 ? copy.folderOne : copy.folderMany}</span>
            </div>
            <div className="media-gallery-head-stat">
              <strong>{mediaSummary.totalMemories}</strong>
              <span>{mediaSummary.totalMemories === 1 ? copy.memoryOne : copy.memoryMany}</span>
            </div>
            <div className="media-gallery-head-stat">
              <strong>{mediaSummary.taggedMemories}</strong>
              <span>{copy.tagged}</span>
            </div>
            <div className="media-gallery-head-stat">
              <strong>{mediaSummary.releaseLockedMemories}</strong>
              <span>{copy.releaseLocked}</span>
            </div>
          </div>
          <p className="media-gallery-head-support">{copy.highlightsBody}</p>
          <div className="media-gallery-head-pill-row">
            {copy.pills.map((pill) => (
              <span key={pill} className="media-gallery-head-pill">
                {pill}
              </span>
            ))}
          </div>
        </aside>
      </div>

      {!foldersConfigured ? (
        <p className="media-folder-config-hint" role="status">
          {copy.foldersUnavailable}
        </p>
      ) : null}

      {FEATURE_FLAGS.enableWhatsAppImportInbox ? (
        <WhatsAppImportsPanel
          authToken={authToken}
          locale={locale}
          tagOptions={tagOptions}
          folders={folders}
          onImportsChanged={() => {
            loadFolders();
            loadMedia();
            loadLibraryIndex();
            if (typeof onMediaLibraryChanged === "function") {
              onMediaLibraryChanged();
            }
          }}
        />
      ) : null}

      <section className="media-privacy-banner" aria-label={copy.privacyAria}>
        <div className="media-privacy-banner-copy">
          <h3>{copy.privacyTitle}</h3>
          <p>{copy.privacyBody}</p>
        </div>
        <ul className="media-privacy-banner-points">
          {copy.privacyPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <div className="media-library-scope" aria-label={copy.scopeAria}>
        <button
          type="button"
          className={`media-library-scope-btn${libraryScope === "owned" ? " media-library-scope-btn-active" : ""}`}
          onClick={() => setLibraryScope("owned")}
        >
          {copy.scopeOwned}
        </button>
        <button
          type="button"
          className={`media-library-scope-btn${libraryScope === "shared" ? " media-library-scope-btn-active" : ""}`}
          onClick={() => setLibraryScope("shared")}
        >
          {copy.scopeShared}
        </button>
      </div>

      {libraryScope === "owned" ? (
        <>
      <div className="media-folder-toolbar" aria-label={copy.folderFiltersAria}>
        <span className="media-folder-toolbar-label">{copy.view}</span>
        <div className="media-folder-chips">
          <button
            type="button"
            className={`media-folder-chip${galleryFilter === "all" ? " media-folder-chip-active" : ""}`}
            onClick={() => setGalleryFilter("all")}
          >
            {copy.all}
          </button>
          <button
            type="button"
            className={`media-folder-chip${galleryFilter === "ungrouped" ? " media-folder-chip-active" : ""}`}
            onClick={() => setGalleryFilter("ungrouped")}
          >
            {copy.ungrouped}
          </button>
          {folders.map((f) => (
            <span
              key={f.folderId}
              className={`media-folder-chip-wrap${galleryFilter === f.folderId ? " media-folder-chip-wrap-active" : ""}`}
            >
              <button
                type="button"
                className={`media-folder-chip${galleryFilter === f.folderId ? " media-folder-chip-active" : ""}`}
                onClick={() => setGalleryFilter(f.folderId)}
              >
                {f.name}
              </button>
              <button
                type="button"
                className="media-folder-chip-delete"
                aria-label={copy.deleteFolderAria(f.name)}
                title={copy.deleteFolder}
                onClick={(e) => {
                  e.stopPropagation();
                  requestDeleteFolder(f.folderId, f.name);
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

        </>
      ) : null}

      {mediaNotice ? (
        <p className="media-notice-banner" role="status">
          {mediaNotice}
        </p>
      ) : null}

      {libraryScope === "shared" && sharedNotice ? (
        <p className="media-notice-banner" role="status">
          {sharedNotice}
        </p>
      ) : null}

      {localError ? <p className="error-text">{localError}</p> : null}

      <div id="media-gallery-browser" className="media-gallery-toolbar">
        <div className="media-gallery-toolbar-copy">
          <h3>{currentGalleryTitle}</h3>
          <p className="helper-text">{currentGalleryDescription}</p>
        </div>
        <div className="media-gallery-toolbar-actions">
          {libraryScope === "owned" && !showingFolderBrowser ? (
            <button
              type="button"
              className="small-btn secondary"
              onClick={() => setGalleryFilter("all")}
            >
              {copy.backToFolders}
            </button>
          ) : null}
          <button
            type="button"
            className="small-btn"
            onClick={() => {
              loadFolders();
              loadLibraryIndex();
              if (libraryScope === "shared") {
                loadSharedMedia();
              } else {
                loadMedia();
              }
            }}
            disabled={loading}
          >
            {loading ? copy.refreshing : copy.refresh}
          </button>
        </div>
      </div>

      {libraryScope === "shared" ? (
        <>
          {sharedItems.length === 0 && !loading ? (
            <p className="helper-text media-gallery-empty-state">
              {copy.sharedEmpty}
            </p>
          ) : null}

          <ul className="media-gallery-grid">
            {sharedItems.map((item) => (
              <li key={item.mediaId} className="media-gallery-tile">
                <button
                  type="button"
                  className="media-gallery-thumb-btn"
                  onClick={() => setLightbox(item)}
                >
                  {item.viewUrl && isVideo(item.contentType) ? (
                    <video
                      className="media-thumb"
                      src={item.viewUrl}
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : item.viewUrl ? (
                    <img
                      className="media-thumb"
                      src={item.viewUrl}
                      alt={item.fileName || copy.sharedMemoryFallback}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="media-thumb media-thumb-fallback">{copy.previewUnavailable}</div>
                  )}
                  <span className="media-gallery-thumb-title">{item.fileName || copy.untitled}</span>
                </button>
                {item.sharedByName ? (
                  <span className="media-shared-by-pill">{copy.sharedBy(item.sharedByName)}</span>
                ) : null}
                {item.taggedDisplayName ? (
                  <span className="media-tag-pill">{copy.forYou}</span>
                ) : null}
                {item.memoryMessage ? (
                  <p className="media-memory-snippet">{item.memoryMessage}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : showingFolderBrowser ? (
        <>
          {showOnboardingCard ? (
            <section className="media-onboarding-card" aria-label={copy.onboardingAria}>
              <div>
                <p className="media-onboarding-kicker">{copy.onboardingKicker}</p>
                <h4>{copy.onboardingTitle}</h4>
                <ul className="media-onboarding-points">
                  {copy.onboardingPoints.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                className="small-btn"
                onClick={() => {
                  setShowUploadComposer(true);
                  document
                    .getElementById("media-library-actions")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {copy.addFirstMemory}
              </button>
            </section>
          ) : null}

          <div className="media-folder-browser-grid">
            {folderCards.map((card) => (
              <button
                key={card.id}
                type="button"
                className="media-folder-browser-card"
                onClick={() => setGalleryFilter(card.id)}
              >
                <div className="media-folder-browser-preview" aria-hidden="true">
                  <div className="media-folder-browser-preview-badge">
                    <span className="media-folder-browser-preview-icon">{card.categoryIcon}</span>
                    <span>{card.categoryLabel}</span>
                  </div>
                  {card.previews.length > 0 ? (
                    card.previews.map((preview) =>
                      preview.viewUrl && isVideo(preview.contentType) ? (
                        <video
                          key={preview.mediaId}
                          className="media-folder-browser-preview-item"
                          src={preview.viewUrl}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : preview.viewUrl ? (
                        <img
                          key={preview.mediaId}
                          className="media-folder-browser-preview-item"
                          src={preview.viewUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div
                          key={preview.mediaId}
                          className="media-folder-browser-preview-item media-folder-browser-preview-fallback"
                        >
                          {copy.previewUnavailable}
                        </div>
                      )
                    )
                  ) : (
                    <div className="media-folder-browser-empty">{copy.emptyFolder}</div>
                  )}
                  <div className="media-folder-browser-hover-overlay">
                    <span>{copy.openFolder}</span>
                  </div>
                </div>
                <div className="media-folder-browser-card-body">
                  <div>
                    <strong>{card.name}</strong>
                    <p>{card.subtitle}</p>
                  </div>
                  <span className="media-folder-browser-open">{copy.openFolder}</span>
                </div>
                <div className="media-folder-browser-foot">
                  <span className="media-folder-browser-meta">{formatMemoryCount(card.count, copy)}</span>
                  <span className="media-folder-browser-meta">{card.updatedLabel}</span>
                </div>
              </button>
            ))}
          </div>

          {folderCards.length === 0 || (folderCards.length === 1 && folderCards[0].count === 0) ? (
            <p className="helper-text media-gallery-empty-state">
              {copy.noMemoriesYet}
            </p>
          ) : null}
        </>
      ) : (
        <>
          {items.length === 0 && !loading ? (
            <p className="helper-text">
              {copy.noMemoriesInView}
            </p>
          ) : null}

          <ul className="media-gallery-grid">
            {items.map((item) => (
              <li key={item.mediaId} className="media-gallery-tile">
                <button
                  type="button"
                  className="media-gallery-thumb-btn"
                  onClick={() => setLightbox(item)}
                >
                  {item.viewUrl && isVideo(item.contentType) ? (
                    <video
                      className="media-thumb"
                      src={item.viewUrl}
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : item.viewUrl ? (
                    <img
                      className="media-thumb"
                      src={item.viewUrl}
                      alt={item.fileName || copy.memoryFallback}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="media-thumb media-thumb-fallback">{copy.previewUnavailable}</div>
                  )}
                  <span className="media-gallery-thumb-title">{item.fileName || copy.untitled}</span>
                </button>
                <button
                  type="button"
                  className="media-gallery-delete"
                  aria-label={copy.deleteItemAria(item.fileName)}
                  onClick={() => removeMedia(item.mediaId)}
                >
                  ×
                </button>
                {item.folderId && folderNameById.get(item.folderId) ? (
                  <span className="media-folder-pill">{folderNameById.get(item.folderId)}</span>
                ) : null}
                {item.taggedDisplayName ? (
                  <span className="media-tag-pill">{copy.forPerson(item.taggedDisplayName)}</span>
                ) : null}
                {item.sourceChannel === "whatsapp" ? (
                  <span className="media-source-pill">{copy.sourceWhatsApp}</span>
                ) : null}
                {item.recipientAccessAfterRelease ? (
                  <span className="media-release-pill" title={copy.recipientAccessAfterRelease}>
                    {copy.afterRelease}
                  </span>
                ) : null}
                {item.memoryMessage ? (
                  <p className="media-memory-snippet">{item.memoryMessage}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}

      {libraryScope === "owned" ? (
        <section
          id="media-library-actions"
          className="media-actions-panel"
          aria-label={copy.actionsAria}
        >
          <div className="media-actions-head">
            <div>
              <h3>{copy.actionsTitle}</h3>
              <p className="helper-text">{copy.actionsBody}</p>
            </div>
            <div className="media-actions-toggle-row">
              {foldersConfigured ? (
                <button
                  type="button"
                  className={`small-btn secondary media-action-toggle${showFolderComposer ? " media-action-toggle-active" : ""}`}
                  onClick={() => setShowFolderComposer((current) => !current)}
                >
                  {showFolderComposer ? copy.hideSpaceCreator : copy.createSpace}
                </button>
              ) : null}
              <button
                type="button"
                className={`small-btn secondary media-action-toggle${showUploadComposer ? " media-action-toggle-active" : ""}`}
                onClick={() => setShowUploadComposer((current) => !current)}
              >
                {showUploadComposer ? copy.hideAddMemory : copy.addMemory}
              </button>
            </div>
          </div>

          {foldersConfigured && showFolderComposer ? (
            <div className="media-folder-create media-action-card">
              <div className="media-folder-create-row">
                <label htmlFor="media-new-folder-name" className="field-label">
                  {copy.nameSpace}
                </label>
                <div className="media-folder-create-inputs">
                  <input
                    id="media-new-folder-name"
                    type="text"
                    placeholder={copy.spacePlaceholder}
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    disabled={creatingFolder}
                    maxLength={200}
                  />
                  <button
                    type="button"
                    className="small-btn"
                    disabled={creatingFolder || !newFolderName.trim()}
                    onClick={() => createFolder(newFolderName)}
                  >
                    {creatingFolder ? copy.creating : copy.createSpaceAction}
                  </button>
                </div>
              </div>
              <p className="helper-text media-folder-create-note">
                {copy.folderCreateNote}
              </p>
              <div className="media-folder-templates" aria-label={copy.quickIdeasAria}>
                <span className="helper-text">{copy.quickStarts}</span>
                {copy.templates.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    className="media-folder-template-btn"
                    disabled={creatingFolder}
                    onClick={() => createFolder(t.name)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showUploadComposer ? (
            <div
              className={`media-dropzone media-action-card ${dragActive && !uploadsDisabled ? "media-dropzone-active" : ""} ${uploadsDisabled ? "media-dropzone-disabled" : ""}`}
              onDragEnter={(e) => {
                if (uploadsDisabled) return;
                e.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(e) => {
                if (uploadsDisabled) return;
                e.preventDefault();
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (e.currentTarget === e.target) setDragActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (uploadsDisabled) return;
                const files = e.dataTransfer?.files;
                if (files?.length) onPickFiles(files);
              }}
            >
              <div className="media-upload-head">
                <div>
                  <h4>{uploadFolderId ? copy.uploadToSpace : copy.uploadTitle}</h4>
                  <p className="helper-text">{copy.uploadBody}</p>
                </div>
              </div>

              {foldersConfigured ? (
                <>
                  <label htmlFor="media-upload-folder" className="field-label">
                    {copy.addTo}
                  </label>
                  <select
                    id="media-upload-folder"
                    value={uploadFolderId}
                    onChange={(e) => setUploadFolderId(e.target.value)}
                    disabled={uploadsDisabled}
                  >
                    <option value="">{copy.ungroupedNoFolder}</option>
                    {folders.map((f) => (
                      <option key={f.folderId} value={f.folderId}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </>
              ) : null}

              <div className="media-upload-row">
                <label className="media-file-label">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    disabled={uploadsDisabled}
                    onChange={(e) => {
                      onPickFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <span className="media-file-button">
                    {uploading
                      ? copy.adding
                      : !mediaAvailable
                        ? copy.addingDisabled
                        : copy.chooseMemories}
                  </span>
                </label>
                <span className="helper-text media-drop-hint">
                  {!mediaAvailable
                    ? copy.addingUnavailable
                    : copy.dragDrop}
                </span>
              </div>

              <label htmlFor="media-tag-upload" className="field-label">
                {copy.tagRecipientOptional}
              </label>
              <p className="helper-text media-tag-upload-hint">
                {copy.tagRecipientHint}
              </p>
              <select
                id="media-tag-upload"
                value={tagForUpload}
                onChange={(e) => {
                  setTagForUpload(e.target.value);
                  if (!e.target.value) setRecipientAfterRelease(false);
                }}
                disabled={uploadsDisabled}
              >
                <option value="">{copy.notTagged}</option>
                {selectedUploadFolder?.defaultTaggedUserId &&
                !tagOptions.some((o) => o.id === selectedUploadFolder.defaultTaggedUserId) ? (
                  <option value={selectedUploadFolder.defaultTaggedUserId}>
                    {selectedUploadFolder.defaultTaggedDisplayName || copy.savedRecipient}
                  </option>
                ) : null}
                {tagOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>

              {tagForUpload ? (
                <>
                  <label className="media-release-checkbox">
                    <input
                      type="checkbox"
                      checked={recipientAfterRelease}
                      onChange={(e) => setRecipientAfterRelease(e.target.checked)}
                      disabled={uploadsDisabled}
                    />
                    <span>
                      {copy.releaseCheckbox}
                    </span>
                  </label>
                  <p
                    className={`media-share-behavior ${recipientAfterRelease ? "media-share-behavior-delayed" : "media-share-behavior-now"}`}
                  >
                    {recipientAfterRelease
                      ? copy.shareBehaviorDelayed
                      : copy.shareBehaviorNow}
                  </p>
                </>
              ) : null}

              <label htmlFor="media-memory-message" className="field-label">
                {copy.noteForMemories} <span className="profile-optional">(optional)</span>
              </label>
              <p className="helper-text media-memory-note-hint">
                {copy.noteHint}
              </p>
              <textarea
                id="media-memory-message"
                rows={3}
                value={memoryMessageForUpload}
                onChange={(e) => setMemoryMessageForUpload(e.target.value)}
                disabled={uploadsDisabled}
                placeholder={copy.notePlaceholder}
              />

              {foldersConfigured && uploadFolderId && selectedUploadFolder ? (
                <div className="media-folder-default-actions">
                  <div className="media-folder-default-buttons">
                    <button
                      type="button"
                      className="small-btn"
                      disabled={savingFolderDefaults || uploadsDisabled}
                      onClick={saveFolderRecipientDefaults}
                    >
                      {savingFolderDefaults ? copy.saving : copy.saveRecipientForSpace}
                    </button>
                    {selectedUploadFolder.defaultTaggedUserId ? (
                      <button
                        type="button"
                        className="small-btn secondary"
                        disabled={savingFolderDefaults || uploadsDisabled}
                        onClick={clearFolderRecipientDefaults}
                      >
                        {copy.clearSavedRecipient}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {folderDeleteConfirm ? (
        <div
          className="media-folder-delete-modal-overlay"
          role="presentation"
          onClick={cancelDeleteFolder}
        >
          <div
            className="media-folder-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="folder-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="folder-delete-title" className="media-folder-delete-modal-title">
              {copy.deleteFolderQuestion}
            </h3>
            <p className="media-folder-delete-modal-body">
              {copy.deleteFolderBody(folderDeleteConfirm.name)}
            </p>
            <div className="media-folder-delete-modal-actions">
              <button
                type="button"
                className="secondary"
                disabled={deletingFolder}
                onClick={cancelDeleteFolder}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className="media-folder-delete-modal-confirm"
                disabled={deletingFolder}
                onClick={confirmDeleteFolder}
              >
                {deletingFolder ? copy.deleting : copy.deleteFolderAction}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lightbox && (
        <div
          className="media-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={copy.lightboxAria}
          onClick={() => setLightbox(null)}
        >
          <div className="media-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="media-lightbox-close"
              aria-label={copy.close}
              onClick={() => setLightbox(null)}
            >
              ×
            </button>
            {lightbox.viewUrl && isVideo(lightbox.contentType) ? (
              <video className="media-lightbox-media" src={lightbox.viewUrl} controls autoPlay />
            ) : lightbox.viewUrl ? (
              <img
                className="media-lightbox-media"
                src={lightbox.viewUrl}
                alt={lightbox.fileName || ""}
              />
            ) : null}
            <div className="media-lightbox-meta">
              <strong>{lightbox.fileName}</strong>
              {lightbox.sharedByName ? (
                <span className="helper-text">{copy.sharedByLabel(lightbox.sharedByName)}</span>
              ) : null}
              {lightbox.folderId && folderNameById.get(lightbox.folderId) ? (
                <span className="helper-text">{copy.folderLabel(folderNameById.get(lightbox.folderId))}</span>
              ) : null}
              {lightbox.taggedDisplayName ? (
                <span className="helper-text">{copy.taggedLabel(lightbox.taggedDisplayName)}</span>
              ) : null}
              {lightbox.recipientAccessAfterRelease ? (
                <span className="helper-text">{copy.releaseVerification}</span>
              ) : null}
              {lightbox.sourceChannel === "whatsapp" ? (
                <span className="helper-text">{copy.importedWhatsApp}</span>
              ) : null}
              {lightbox.memoryMessage ? (
                <p className="media-lightbox-message">{lightbox.memoryMessage}</p>
              ) : null}
              {!lightbox.isSharedWithMe ? (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => removeMedia(lightbox.mediaId)}
                >
                  {copy.removeFromGallery}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
